import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  IconButton,
  Card,
  Stack,
  Paper
} from '@mui/material';
import { 
  ArrowBack as ArrowBackIcon,
  Search as SearchIcon,
  InboxOutlined as InboxOutlinedIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { queryCouponList } from '../../api/modules/invite';
import { showMessage } from '../../utils';
import { isInApp } from '../../utils/environment';

const PendingOrders = () => {
  const [searchText, setSearchText] = useState('');
  const [orders, setOrders] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    fetchOrders();
  }, [searchText]);

  const fetchOrders = async (name = '') => {
    try {
      const params = {
        bizType: 2,
        ...(searchText && { name: searchText }) // 优化：使用展开运算符简化条件赋值
      };
      await queryCouponList(params, (rst) => {
        const { code, rows } = rst;
        if (code === 200 && rows) {
          setOrders(rows);
        }
      });
    } catch (error) {
        showMessage('Failed to fetch orders', 'error');
    }
  };

  const handleSearch = (event) => {
    const value = event.target.value;
    setSearchText(value);
    fetchOrders(value);
  };

  const EmptyState = () => (
    <Box sx={{ 
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      py: 8,
      px: 2
    }}>
      <InboxOutlinedIcon sx={{ 
        fontSize: 48,
        color: '#9CA3AF',
        mb: 2
      }} />
      <Typography sx={{ 
        color: '#111111',
        fontSize: '1rem',
        fontWeight: 500,
        mb: 1
      }}>
        No Pending Orders
      </Typography>
      <Typography sx={{ 
        color: '#6B7280',
        fontSize: '0.875rem',
        textAlign: 'center'
      }}>
        There are currently no orders pending verification
      </Typography>
    </Box>
  );

  return (
    <Box sx={{ 
      bgcolor: '#F5F5F7',
      minHeight: '100vh'
    }}>
      {/* Header */}
      {
        !isInApp() && (
          <Box sx={{ 
            bgcolor: 'white',
            borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
            position: 'sticky',
            top: 0,
            zIndex: 10
          }}>
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              p: 2,
              gap: 2
            }}>
              <IconButton 
                onClick={() => navigate(-1)}
                edge="start"
              >
                <ArrowBackIcon />
              </IconButton>
              <Typography variant="h6" sx={{ 
                fontSize: '1.25rem',
                fontWeight: 600,
                color: '#111111'
              }}>
                Pending Orders
              </Typography>
            </Box>
          </Box>
        )
      }
        {/* Search Bar */}
        <Box sx={{ px: 2, pb: 2 }}>
          <Paper
            elevation={0}
            sx={{
              mt: '24px',
              p: '6px 6px',
              display: 'flex',
              alignItems: 'center',
              bgcolor: '#fff',
              borderRadius: '12px'
            }}
          >
            <InputAdornment position="start" sx={{ pl: 1 }}>
              <SearchIcon sx={{ color: '#9CA3AF' }} />
            </InputAdornment>
            <TextField
              fullWidth
              placeholder="Search orders..."
              value={searchText}
              onChange={handleSearch}
              variant="standard"
              InputProps={{
                disableUnderline: true,
                sx: { 
                  px: 1,
                  '& input': {
                    color: '#111111',
                    '&::placeholder': {
                      color: '#9CA3AF',
                      opacity: 1
                    }
                  }
                }
              }}
            />
          </Paper>
        </Box>
      {/* Order List */}
      <Stack spacing={2} sx={{ p: 2 }}>
        {orders.length > 0 ? (
          orders.map((order) => (
            <Card
              key={order.couponId}
              elevation={0}
              sx={{
                bgcolor: 'white',
                borderRadius: 2,
                p: 2
              }}
            >
              <Box sx={{ 
                display: 'flex',
                justifyContent: 'space-between',
                mb: 2
              }}>
                <Typography sx={{ 
                  fontWeight: 600,
                  color: '#111111'
                }}>
                  Order #{order.couponId}
                </Typography>
                <Typography sx={{ 
                  color: '#6B7280',
                  fontSize: '0.875rem'
                }}>
                  {new Date(order.createTime).toLocaleString('en-US', {
                    hour: 'numeric',
                    minute: 'numeric',
                    hour12: true
                  })}
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography sx={{ color: '#6B7280' }}>Customer</Typography>
                <Typography sx={{ 
                  color: '#111111',
                  fontWeight: 500
                }}>
                  {order?.firstName || ''} {order?.lastName || ''}
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography sx={{ color: '#6B7280' }}>Amount</Typography>
                <Typography sx={{ 
                  color: '#111111',
                  fontWeight: 600
                }}>
                  ${order.actualPrice}
                </Typography>
              </Box>
            </Card>
          ))
        ) : (
          <EmptyState />
        )}
      </Stack>
    </Box>
  );
};

export default PendingOrders;
